import { useEffect, useState } from 'react';

interface LoadingScreenProps {
  onLoadingComplete: () => void;
}

export default function LoadingScreen({ onLoadingComplete }: LoadingScreenProps) {
  const [progress, setProgress] = useState(0);
  const [loadingText, setLoadingText] = useState('LOADING');
  const [isExiting, setIsExiting] = useState(false);
  const [barColor, setBarColor] = useState('#44c4ff');

  useEffect(() => {
    const progressInterval = setInterval(() => {
      setProgress((prev) => {
        if (prev >= 100) {
          clearInterval(progressInterval);
          setTimeout(() => {
            setIsExiting(true);
            setTimeout(() => {
              onLoadingComplete();
            }, 500);
          }, 200);
          return 100;
        }
        return prev + 5;
      });
    }, 20);

    const textInterval = setInterval(() => {
      setLoadingText((prev) => {
        if (prev === 'LOADING...') return 'LOADING';
        return prev + '.';
      });
    }, 200);

    const colorInterval = setInterval(() => {
      setBarColor((prev) => {
        const colors = ['#44c4ff', '#1ef99d', '#fc457e', '#fcb122'];
        const currentIndex = colors.indexOf(prev);
        return colors[(currentIndex + 1) % colors.length];
      });
    }, 300);

    return () => {
      clearInterval(progressInterval);
      clearInterval(textInterval);
      clearInterval(colorInterval);
    };
  }, [onLoadingComplete]);

  return (
    <div className={`fixed inset-0 z-50 flex items-center justify-center bg-[#0a0a0a] transition-opacity duration-500 ${isExiting ? 'opacity-0' : 'opacity-100'}`}>
      <div className="absolute inset-0 opacity-30" style={{
        backgroundImage: `
          linear-gradient(0deg, transparent 24%, rgba(68, 196, 255, 0.3) 25%, rgba(68, 196, 255, 0.3) 26%, transparent 27%, transparent 74%, rgba(30, 249, 157, 0.3) 75%, rgba(30, 249, 157, 0.3) 76%, transparent 77%, transparent),
          linear-gradient(90deg, transparent 24%, rgba(68, 196, 255, 0.3) 25%, rgba(68, 196, 255, 0.3) 26%, transparent 27%, transparent 74%, rgba(30, 249, 157, 0.3) 75%, rgba(30, 249, 157, 0.3) 76%, transparent 77%, transparent)
        `,
        backgroundSize: '50px 50px'
      }}></div>

      <div className="relative z-10 text-center px-4">
        <div className="mb-12">
          <div className="text-3xl sm:text-4xl md:text-5xl lg:text-6xl font-['Press_Start_2P',_cursive] text-[#44c4ff] mb-6 animate-pulse leading-relaxed" style={{
            textShadow: '0 0 20px #44c4ff, 0 0 40px #44c4ff'
          }}>
            IRINO MIYU
          </div>
          <div className="text-2xl sm:text-3xl md:text-4xl lg:text-5xl font-['Press_Start_2P',_cursive] leading-relaxed">
            <span className="text-[#1ef99d]" style={{
              textShadow: '0 0 20px #1ef99d, 0 0 40px #1ef99d'
            }}>LIVE TOUR </span>
            <span className="text-[#fc457e]" style={{
              textShadow: '0 0 20px #fc457e, 0 0 40px #fc457e'
            }}>2026</span>
          </div>
        </div>

        <div className="text-xl md:text-2xl font-['Press_Start_2P',_cursive] text-white mb-8 h-8">
          {loadingText}
        </div>

        <div className="max-w-md mx-auto mb-4">
          <div className="w-full h-8 bg-[#1a1a3e] border-4 border-[#44c4ff] relative overflow-hidden" style={{
            boxShadow: '0 0 20px rgba(68, 196, 255, 0.5)'
          }}>
            <div 
              className="h-full transition-all duration-200 ease-out relative"
              style={{ 
                width: `${progress}%`,
                backgroundColor: barColor,
                boxShadow: `0 0 20px ${barColor}`
              }}
            >
              <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white to-transparent opacity-30 animate-[shimmer_0.8s_infinite]"></div>
            </div>

            <div className="absolute inset-0 flex">
              {[...Array(20)].map((_, i) => (
                <div 
                  key={i} 
                  className="flex-1 border-r border-[#0a0a0a] opacity-20"
                ></div>
              ))}
            </div>
          </div>
        </div>

        <div className="text-lg md:text-xl font-['Press_Start_2P',_cursive] text-[#fcb122] mb-8">
          {progress}%
        </div>

        <div className="flex justify-center gap-2">
          {[...Array(5)].map((_, i) => (
            <div
              key={i}
              className="w-4 h-4 animate-pulse"
              style={{
                backgroundColor: ['#44c4ff', '#1ef99d', '#fc457e', '#fcb122', '#44c4ff'][i],
                animationDelay: `${i * 0.15}s`,
                boxShadow: `0 0 10px currentColor`
              }}
            ></div>
          ))}
        </div>

        {progress === 100 && (
          <div className="mt-12 text-sm md:text-base font-['Press_Start_2P',_cursive] text-white animate-pulse">
            PRESS START
          </div>
        )}
      </div>

      <style>{`
        @keyframes shimmer {
          0% { transform: translateX(-100%); }
          100% { transform: translateX(100%); }
        }
      `}</style>
    </div>
  );
}